import { FC, useId } from 'react';
import { Control, useController, useWatch } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';

import type { TemplateRow } from '../../../documents/templates/components/table/row/template-row.types';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../../components/combobox/index';
import Loader from '../../../../components/loader/index';
import { selectedCompanyStorage } from '../../../../storage/company/index';
import { PageRoute } from '../../../../types/page.types';
import { useInfiniteTemplates } from '../../../documents/templates/hooks/use-infinite-templates.hook';
import { FormSchema } from '../event-form.validation';

const TEMPLATE_FIELD = 'stepOne.eventDetails.grant.selectedTemplateId' as const;

type DocumentTemplateOptionListProps = {
  control: Control<FormSchema>;
  fetchNextPage: () => void;
  hasEquityPlan: boolean;
  hasNextPage: boolean;
  isLoading: boolean;
  scrollableTargetId: string;
  templates: TemplateRow[];
};

const DocumentTemplateOptionList: FC<DocumentTemplateOptionListProps> = ({
  control,
  fetchNextPage,
  hasEquityPlan,
  hasNextPage,
  isLoading,
  scrollableTargetId,
  templates,
}) => {
  const navigate = useNavigate();
  const content = (() => {
    if (!hasEquityPlan) {
      return (
        <span className="py-2 text-sm font-normal text-gray-500">
          <AppFormattedMessage id="EQUITY_PLAN_REQUIRED_GRANT" />
        </span>
      );
    }
    if (isLoading) {
      return (
        <div className="flex justify-center py-4">
          <Loader />
        </div>
      );
    }
    if (templates.length === 0) {
      return (
        <span className="py-2 text-sm font-normal text-gray-700">
          <AppFormattedMessage id="NO_TEMPLATE_AVAILABLE_FOR_PLAN" />
        </span>
      );
    }
    return (
      <InfiniteScroll
        className="flex flex-col gap-1"
        dataLength={templates.length}
        hasMore={hasNextPage}
        loader={
          <div className="flex justify-center py-2">
            <Loader />
          </div>
        }
        next={fetchNextPage}
        scrollableTarget={scrollableTargetId}
      >
        {templates.map((template) => (
          <FormComboboxOption
            control={control}
            key={template.id}
            name={TEMPLATE_FIELD}
            value={template.id}
          >
            <span className="truncate text-sm font-normal text-gray-700">{template.name}</span>
          </FormComboboxOption>
        ))}
      </InfiniteScroll>
    );
  })();

  return (
    <div className="flex w-full flex-col gap-2">
      {content}
      <Button
        className="w-full justify-start text-nowrap rounded bg-brand-25 px-2 py-[6px] text-start text-sm font-[450] text-brand-700"
        onClick={() => navigate(PageRoute.DOCUMENTS_TEMPLATES)}
        styleType="NONE"
      >
        + <AppFormattedMessage id="ADD_TEMPLATE" />
      </Button>
    </div>
  );
};

export type DocumentTemplateComboboxProps = {
  control: Control<FormSchema>;
};

export const DocumentTemplateCombobox: FC<DocumentTemplateComboboxProps> = ({ control }) => {
  const scrollableTargetId = useId();

  const equityPlanId = useWatch({
    control,
    name: 'stepOne.eventDetails.grant.sharePlan',
    defaultValue: undefined,
  })?.id;

  const { field } = useController({ control, name: TEMPLATE_FIELD });
  const selectedId = field.value ?? '';
  const companyId = selectedCompanyStorage(({ companyId }) => companyId);

  const { templates, fetchNextPage, hasNextPage, isPending } = useInfiniteTemplates({
    companyId,
    equityPlanId: equityPlanId ?? undefined,
    enabled: Boolean(companyId && equityPlanId),
  });

  const selectedTemplate = templates.find((t) => t.id === selectedId);
  const displayValue = selectedTemplate?.name ?? '';

  return (
    <FormCombobox className="relative" control={control} name={TEMPLATE_FIELD}>
      <div className="relative">
        <FormComboboxInput
          control={control}
          customValue={() => displayValue}
          icon={<ChevronDownIcon className="mt-3 h-4 w-4 text-gray-500" />}
          name={TEMPLATE_FIELD}
          placeholder={<AppFormattedMessage id="TEMPLATE" />}
          readOnly
          wrapperClassName="min-h-[56px]"
        />
        <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
      </div>
      <ComboboxOptions
        className="absolute z-10 max-h-[280px] w-full overflow-y-auto rounded border border-gray-200 bg-white p-2 shadow-lg"
        id={scrollableTargetId}
      >
        <DocumentTemplateOptionList
          control={control}
          fetchNextPage={fetchNextPage}
          hasEquityPlan={Boolean(equityPlanId)}
          hasNextPage={!!hasNextPage}
          isLoading={isPending}
          scrollableTargetId={scrollableTargetId}
          templates={templates}
        />
      </ComboboxOptions>
    </FormCombobox>
  );
};
